import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type GetServiceSchema, getServiceSchema } from "@msuite/utils/schemas";
import type { NullableFields, Tables } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { AddServiceToContactForm } from "@/forms/add-service-to-contact-form";
import { client } from "@/hono";
import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";

/** Props Interface */
interface PickServiceModal extends ModalProps {
	onComplete: (service: NullableFields<Tables<"organizationServices">>) => Promise<void>;
	onCompleteCustom?: () => void;
}

export function PickServiceModal({
	isOpen,
	onClose,
	onComplete,
	onCompleteCustom,
}: PickServiceModal) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(getServiceSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: GetServiceSchema) {
		try {
			const response = await client.organizations.services[":serviceId"].$get({
				param: { serviceId: json.serviceId },
			});
			const data = await responseHandler(response);
			if (!data.service) throw new Error("Service not found");
			await onComplete(data.service);
			onClose?.();
		} catch (error) {
			console.error(error);
		}
	}

	function handleOnAddCustomService() {
		onCompleteCustom?.();
		onClose?.();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("organization.data.select_service")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					<AddServiceToContactForm />
					<div className="h-stack justify-end">
						<Button
							type="button"
							variant="white"
							icon={<Icon name="Plus" />}
							onClick={handleOnAddCustomService}
						>
							{t("organization.data.add_custom_service")}
						</Button>
					</div>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
